import { useSuspenseQuery } from "@tanstack/react-query";

import { AudioSlot } from "@/features/audio/kiosk-audio";
import {
  isStartFailed,
  orderStatusQuery,
} from "@/features/wash-start-guide/order-status";
import { WashStartGuideScreen } from "@/features/wash-start-guide/wash-start-guide-screen";
import type { OrderStatus } from "@/lib/server";

/**
 * The wash start guide fed by the order status poll: the drum spinning moves
 * it to the success view, a used-up start window to the failure screen.
 */
export function WashStartGuide({
  orderId,
  startTimeoutSeconds,
  currencySymbol,
  onRequestInvoice,
  onStartAnother,
  onFinish,
}: {
  orderId: string;
  // fixed by the caller, so the countdown bar doesn't jump on every poll
  startTimeoutSeconds: number;
  currencySymbol: string;
  // set when the purchase can get an invoice
  onRequestInvoice?: () => void;
  onStartAnother: () => void;
  onFinish: () => void;
}) {
  const { data: status } = useSuspenseQuery(orderStatusQuery(orderId));

  return (
    <>
      <AudioSlot slot={audioSlotFor(status)} />
      <WashStartGuideScreen
        machineNumber={status.machine.storePlace}
        machineCapacity={status.machine.capacity}
        isRunning={status.spinningState === "spinning"}
        failed={isStartFailed(status)}
        startTimeoutSeconds={startTimeoutSeconds}
        sessionId={status.sessionId ?? ""}
        collectorsDiscount={status.collectorsDiscount}
        currencySymbol={currencySymbol}
        invoice={onRequestInvoice ? { onRequest: onRequestInvoice } : undefined}
        onStartAnother={onStartAnother}
        onFinish={onFinish}
      />
    </>
  );
}

export function audioSlotFor(status: OrderStatus) {
  return isStartFailed(status)
    ? "page.wash-start-failed"
    : status.spinningState === "spinning"
      ? "page.wash-start-success"
      : "page.wash-start-guide";
}
